/**
 * Binds the telemetry auth gate to Firebase auth. One line per PWA entry file.
 *
 * WHY THIS IS A SEPARATE MODULE FROM `authGate.ts`. The gate itself is pure
 * policy and dependency-free, so it can be tested without a Firebase runtime.
 * This file is the only place that touches the SDK. It imports `firebase/auth`,
 * a subpath of the `firebase` package that every client already depends on and
 * already imports in its own `config/firebase` — so it adds no bare specifier
 * that a consumer lacks (the failure mode `buildIdentity.ts` documents).
 *
 * 🔴 WHY `onAuthStateChanged` AND NOT AN APPLICATION PROFILE FIELD. A
 * `withOrgAuth` callable accepts or rejects a call based on one thing: whether
 * the request carries a valid Firebase ID token. Gating on an application field
 * such as `user.userId` would be gating on something the callable never reads —
 * it is absent on a sizeable minority of user documents, so that gate would
 * silently drop those users' telemetry while reporting itself as healthy.
 *
 * `onAuthStateChanged` also fires once on startup with the restored session (or
 * `null`), which is exactly the "auth has now resolved" edge the gate's third
 * state is waiting for.
 */
import { onAuthStateChanged, type Auth } from 'firebase/auth';
import { setTelemetryAuthState } from './authGate';

/**
 * Start reporting Firebase auth state to the telemetry gate.
 *
 * Returns the unsubscribe function. Entry files call this once at startup and
 * do not unsubscribe — the subscription lives as long as the page does.
 */
export function bindTelemetryAuthGate(auth: Auth): () => void {
  return onAuthStateChanged(
    auth,
    (user) => setTelemetryAuthState(user ? 'authed' : 'anonymous'),
    // An auth error is a RESOLUTION, not a reason to buffer forever: without
    // this the gate would sit in `unknown` and quietly accumulate events that
    // can never be sent. Resolving to anonymous makes the loss counted.
    () => setTelemetryAuthState('anonymous'),
  );
}
